import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

import { createIconComponent } from 'uikit/icons';

import Close from '@mui/icons-material/Close';

import Button from '../../buttons/components/button';
import Markdown from '../../content/components/markdown';
import Typo from '../../content/components/typo';

import styles from '../styles/uikitBannersCallout.module.scss';

const VARIANTS = {
  ai: styles.isVariantAi,
  caution: styles.isVariantCaution,
  success: styles.isVariantSuccess,
  warning: styles.isVariantWarning,
};

const Callout = ({
  actions = null,
  className = '',
  closeButtonAriaLabel = i18n.t('close', 'Close'),
  hasCloseButton = false,
  hasMarkdownText = false,
  heading = null,
  icon = <InfoOutlineIcon />,
  id = '',
  onCloseButtonClick = null,
  subText = null,
  text,
  textVariant = 'b02',
  variant = 'default',
}) => {
  const getText = () => (
    <>
      {heading && (
        <Typo className={styles.textHeading} variant="s03" weight="medium">
          {heading}
        </Typo>
      )}
      {hasMarkdownText ? (
        <>
          <Markdown
            className={classnames(styles.textBodyMarkdown, {
              [styles.isTextVariantSupport02]: textVariant === 's02',
            })}
            content={text}
          />
          {subText && <Markdown className={styles.subTextBodyMarkdown} content={subText} />}
        </>
      ) : (
        <>
          <Typo className={styles.textBody} element="div" variant={textVariant}>
            {text}
          </Typo>
          {subText && (
            <Typo className={styles.subTextBody} element="div" variant="b01">
              {subText}
            </Typo>
          )}
        </>
      )}
    </>
  );

  return (
    <div
      className={classnames(
        styles.element,
        {
          [VARIANTS[variant]]: variant,
        },
        className,
      )}
      id={id}
    >
      <div className={styles.content}>
        <div className={styles.info}>
          <div className={styles.iconContainer}>{createIconComponent({ icon, size: '24' })}</div>
          <div className={styles.text}>{getText()}</div>
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {hasCloseButton && (
        <div>
          <Button
            ariaLabel={closeButtonAriaLabel}
            icon={<Close />}
            onClick={onCloseButtonClick || (() => {})}
            size="xsmall"
            variant="ghost"
            className={styles.closeButton}
          />
        </div>
      )}
    </div>
  );
};

const sharedPropTypes = {
  actions: PropTypes.node,
  className: PropTypes.string,
  closeButtonAriaLabel: PropTypes.string,
  hasCloseButton: PropTypes.bool,
  hasMarkdownText: PropTypes.bool,
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onCloseButtonClick: PropTypes.func,
  subText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  variant: PropTypes.oneOf(['default', 'ai', 'caution', 'success', 'warning']),
};

Callout.propTypes = {
  ...sharedPropTypes,
  id: PropTypes.string,
  textVariant: PropTypes.oneOf(['b02', 's02']),
};

Callout.sharedPropTypes = sharedPropTypes;

export default Callout;
